"use client";

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import YourBarrioLogo from "@/components/brand/YourBarrioLogo";

import styles from "./HomepageVideoHero.module.css";

export const HOMEPAGE_HERO_VIDEO_SRC =
  "/videos/homepage/YourBarrio-movie1.mp4";
export const HOMEPAGE_HERO_POSTER_SRC =
  "/images/homepage/hero-main-1280.webp";
export const HOMEPAGE_HERO_OPENING_FRAME_SRC =
  "/images/homepage/yourbarrio-hero-opening-frame.webp";
export const HOMEPAGE_HERO_REVEAL_LEAD_SECONDS = 2.5;
export const HOMEPAGE_HERO_END_HOLD_MS = 1000;
export const HOMEPAGE_HERO_BRIDGE_IN_MS = 750;
export const HOMEPAGE_HERO_BRIDGE_OUT_MS = 550;
export const HOMEPAGE_HERO_PLAYBACK_RETRY_MS = 650;

const TRANSITION_EVENT_GUARD_MS = 180;
const OPENING_FRAME_GUARD_MS = 1600;

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

export function shouldRevealHomepageBrand(
  currentTime: number,
  duration: number,
  leadSeconds = HOMEPAGE_HERO_REVEAL_LEAD_SECONDS
) {
  if (
    !Number.isFinite(currentTime) ||
    !Number.isFinite(duration) ||
    duration <= 0
  ) {
    return false;
  }

  return currentTime >= Math.max(0, duration - leadSeconds);
}

function subscribeToReducedMotion(callback: () => void) {
  if (typeof window.matchMedia !== "function") return () => {};

  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  mediaQuery.addEventListener?.("change", callback);
  return () => mediaQuery.removeEventListener?.("change", callback);
}

function getReducedMotionSnapshot() {
  if (typeof window.matchMedia !== "function") return "no-preference";
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "reduce"
    : "no-preference";
}

function getServerReducedMotionSnapshot() {
  return "unknown";
}

function useReducedMotionPreference() {
  return useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getServerReducedMotionSnapshot
  );
}

function subscribeToDocumentLoad(callback: () => void) {
  if (document.readyState === "complete") return () => {};
  window.addEventListener("load", callback, { once: true });
  return () => window.removeEventListener("load", callback);
}

function getDocumentLoadSnapshot() {
  return document.readyState === "complete";
}

function getServerDocumentLoadSnapshot() {
  return false;
}

function useDocumentLoaded() {
  return useSyncExternalStore(
    subscribeToDocumentLoad,
    getDocumentLoadSnapshot,
    getServerDocumentLoadSnapshot
  );
}

export default function HomepageVideoHero() {
  const heroRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const sourceRef = useRef<HTMLSourceElement>(null);
  const heroVisibleRef = useRef(false);
  const playAttemptRef = useRef(0);
  const attemptPlaybackRef = useRef<() => void>(() => {});
  const playbackFailureCountRef = useRef(0);
  const loopTransitionActiveRef = useRef(false);
  const openingResetStartedRef = useRef(false);
  const holdTimerRef = useRef<number | null>(null);
  const coverGuardTimerRef = useRef<number | null>(null);
  const openingFrameGuardTimerRef = useRef<number | null>(null);
  const revealTimerRef = useRef<number | null>(null);
  const playbackRecoveryTimerRef = useRef<number | null>(null);
  const openingFrameCleanupRef = useRef<(() => void) | null>(null);
  const resetResumeRef = useRef<(() => void) | null>(null);
  const motionPreference = useReducedMotionPreference();
  const documentLoaded = useDocumentLoaded();
  const motionPreferenceResolved = motionPreference !== "unknown";
  const prefersReducedMotion = motionPreference === "reduce";
  const [videoLoadEnabled, setVideoLoadEnabled] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [brandVisible, setBrandVisible] = useState(false);
  const [openingBridgeVisible, setOpeningBridgeVisible] = useState(false);
  const [loopTransition, setLoopTransition] = useState<
    "idle" | "holding" | "covering" | "resetting" | "revealing"
  >("idle");

  const staticFallback =
    videoFailed ||
    autoplayBlocked ||
    (motionPreferenceResolved && prefersReducedMotion);

  const clearLoopTransitionTimers = useCallback(() => {
    for (const timerRef of [
      holdTimerRef,
      coverGuardTimerRef,
      openingFrameGuardTimerRef,
      revealTimerRef,
      playbackRecoveryTimerRef,
    ]) {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
    openingFrameCleanupRef.current?.();
    openingFrameCleanupRef.current = null;
    resetResumeRef.current = null;
  }, []);

  const syncBrandReveal = useCallback(
    (video: HTMLVideoElement) => {
      if (loopTransitionActiveRef.current) return;
      const nextVisible = shouldRevealHomepageBrand(
        video.currentTime || 0,
        video.duration
      );
      setBrandVisible((current) =>
        current === nextVisible ? current : nextVisible
      );
    },
    []
  );

  const attemptPlayback = useCallback(() => {
    const video = videoRef.current;
    if (
      !video ||
      !videoLoadEnabled ||
      !motionPreferenceResolved ||
      prefersReducedMotion ||
      videoFailed ||
      loopTransitionActiveRef.current ||
      !heroVisibleRef.current ||
      document.visibilityState === "hidden" ||
      !video.paused
    ) {
      return;
    }

    const attemptId = ++playAttemptRef.current;
    try {
      const playback = video.play();
      if (playback && typeof playback.then === "function") {
        playback
          .then(() => {
            if (playAttemptRef.current === attemptId) {
              if (playbackRecoveryTimerRef.current !== null) {
                window.clearTimeout(playbackRecoveryTimerRef.current);
                playbackRecoveryTimerRef.current = null;
              }
              playbackFailureCountRef.current = 0;
              setAutoplayBlocked(false);
              setVideoPlaying(true);
            }
          })
          .catch((error) => {
            if (
              playAttemptRef.current === attemptId &&
              error?.name !== "AbortError"
            ) {
              setVideoPlaying(false);
              playbackFailureCountRef.current += 1;
              if (playbackFailureCountRef.current >= 2) {
                setAutoplayBlocked(true);
                return;
              }

              if (playbackRecoveryTimerRef.current !== null) {
                window.clearTimeout(playbackRecoveryTimerRef.current);
              }
              playbackRecoveryTimerRef.current = window.setTimeout(() => {
                playbackRecoveryTimerRef.current = null;
                attemptPlaybackRef.current();
              }, HOMEPAGE_HERO_PLAYBACK_RETRY_MS);
            }
          });
      } else {
        playbackFailureCountRef.current = 0;
        setAutoplayBlocked(false);
        setVideoPlaying(true);
      }
    } catch {
      setVideoPlaying(false);
      playbackFailureCountRef.current += 1;
      if (playbackFailureCountRef.current >= 2) {
        setAutoplayBlocked(true);
      } else {
        playbackRecoveryTimerRef.current = window.setTimeout(
          () => attemptPlaybackRef.current(),
          HOMEPAGE_HERO_PLAYBACK_RETRY_MS
        );
      }
    }
  }, [
    motionPreferenceResolved,
    prefersReducedMotion,
    videoLoadEnabled,
    videoFailed,
  ]);

  useEffect(() => {
    attemptPlaybackRef.current = attemptPlayback;
  }, [attemptPlayback]);

  const pausePlayback = useCallback(() => {
    playAttemptRef.current += 1;
    videoRef.current?.pause();
  }, []);

  const handleVideoError = useCallback(() => {
    playAttemptRef.current += 1;
    clearLoopTransitionTimers();
    loopTransitionActiveRef.current = false;
    setLoopTransition("idle");
    setOpeningBridgeVisible(false);
    setVideoReady(false);
    setVideoPlaying(false);
    setVideoFailed(true);
    setBrandVisible(false);
  }, [clearLoopTransitionTimers]);

  const finishLoopTransition = useCallback(() => {
    clearLoopTransitionTimers();
    openingResetStartedRef.current = false;
    loopTransitionActiveRef.current = false;
    setOpeningBridgeVisible(false);
    setLoopTransition("idle");
  }, [clearLoopTransitionTimers]);

  const beginCoveredLoopReset = useCallback(
    (video: HTMLVideoElement) => {
      if (
        !loopTransitionActiveRef.current ||
        openingResetStartedRef.current
      ) {
        return;
      }

      openingResetStartedRef.current = true;
      if (coverGuardTimerRef.current !== null) {
        window.clearTimeout(coverGuardTimerRef.current);
        coverGuardTimerRef.current = null;
      }

      video.pause();
      setVideoPlaying(false);
      setBrandVisible(false);
      setLoopTransition("resetting");

      const frameVideo = video as VideoWithFrameCallback;
      let frameCallbackHandle: number | null = null;
      let seeked = false;
      let openingReadyHandled = false;
      let playbackPending = false;
      let playbackStarted = false;
      let playbackAttempts = 0;
      let disposed = false;

      const cleanupOpeningFrameWait = () => {
        if (disposed) return;
        disposed = true;
        video.removeEventListener("seeked", handleSeeked);
        if (
          frameCallbackHandle !== null &&
          typeof frameVideo.cancelVideoFrameCallback === "function"
        ) {
          frameVideo.cancelVideoFrameCallback(frameCallbackHandle);
        }
        if (openingFrameGuardTimerRef.current !== null) {
          window.clearTimeout(openingFrameGuardTimerRef.current);
          openingFrameGuardTimerRef.current = null;
        }
        if (playbackRecoveryTimerRef.current !== null) {
          window.clearTimeout(playbackRecoveryTimerRef.current);
          playbackRecoveryTimerRef.current = null;
        }
      };

      const revealDecodedOpeningVideo = () => {
        if (
          disposed ||
          openingReadyHandled ||
          !loopTransitionActiveRef.current
        ) {
          return;
        }
        openingReadyHandled = true;
        cleanupOpeningFrameWait();
        openingFrameCleanupRef.current = null;
        resetResumeRef.current = null;
        setLoopTransition("revealing");
        setOpeningBridgeVisible(false);
        revealTimerRef.current = window.setTimeout(
          finishLoopTransition,
          HOMEPAGE_HERO_BRIDGE_OUT_MS + TRANSITION_EVENT_GUARD_MS
        );
      };

      const startOpeningPlayback = () => {
        if (!loopTransitionActiveRef.current) return;
        if (
          !heroVisibleRef.current ||
          document.visibilityState === "hidden"
        ) {
          resetResumeRef.current = startOpeningPlayback;
          return;
        }
        if (playbackPending || playbackStarted) return;

        playbackAttempts += 1;
        playbackPending = true;
        try {
          const playback = video.play();
          Promise.resolve(playback)
            .then(() => {
              if (!loopTransitionActiveRef.current) return;
              playbackPending = false;
              playbackStarted = true;
              playbackFailureCountRef.current = 0;
              setAutoplayBlocked(false);
              setVideoPlaying(true);
              if (
                typeof frameVideo.requestVideoFrameCallback === "function"
              ) {
                frameCallbackHandle =
                  frameVideo.requestVideoFrameCallback(
                    revealDecodedOpeningVideo
                  );
                return;
              }
              window.requestAnimationFrame(() => {
                window.requestAnimationFrame(revealDecodedOpeningVideo);
              });
            })
            .catch((error) => {
              playbackPending = false;
              if (error?.name === "AbortError") return;
              setVideoPlaying(false);
              if (playbackAttempts < 2) {
                playbackRecoveryTimerRef.current = window.setTimeout(
                  startOpeningPlayback,
                  HOMEPAGE_HERO_PLAYBACK_RETRY_MS
                );
                return;
              }

              cleanupOpeningFrameWait();
              openingFrameCleanupRef.current = null;
              setAutoplayBlocked(true);
              finishLoopTransition();
            });
        } catch {
          playbackPending = false;
          if (playbackAttempts < 2) {
            playbackRecoveryTimerRef.current = window.setTimeout(
              startOpeningPlayback,
              HOMEPAGE_HERO_PLAYBACK_RETRY_MS
            );
            return;
          }
          cleanupOpeningFrameWait();
          openingFrameCleanupRef.current = null;
          setAutoplayBlocked(true);
          finishLoopTransition();
        }
      };

      function handleSeeked() {
        if (seeked || disposed) return;
        seeked = true;
        startOpeningPlayback();
      }

      openingFrameCleanupRef.current = cleanupOpeningFrameWait;
      video.addEventListener("seeked", handleSeeked);
      const guardOpeningFrame = () => {
        if (disposed || openingReadyHandled) return;
        if (playbackStarted) {
          revealDecodedOpeningVideo();
          return;
        }
        if (!playbackPending) startOpeningPlayback();
        openingFrameGuardTimerRef.current = window.setTimeout(
          guardOpeningFrame,
          500
        );
      };
      openingFrameGuardTimerRef.current = window.setTimeout(
        guardOpeningFrame,
        OPENING_FRAME_GUARD_MS
      );
      video.currentTime = 0;
    },
    [finishLoopTransition]
  );

  const startConcealedLoopReset = useCallback(
    (video: HTMLVideoElement) => {
      if (
        prefersReducedMotion ||
        videoFailed ||
        loopTransitionActiveRef.current
      ) {
        return;
      }

      clearLoopTransitionTimers();
      openingResetStartedRef.current = false;
      loopTransitionActiveRef.current = true;
      setBrandVisible(true);
      setOpeningBridgeVisible(false);
      setLoopTransition("holding");

      holdTimerRef.current = window.setTimeout(() => {
        setLoopTransition("covering");
        setOpeningBridgeVisible(true);
        coverGuardTimerRef.current = window.setTimeout(
          () => beginCoveredLoopReset(video),
          HOMEPAGE_HERO_BRIDGE_IN_MS + TRANSITION_EVENT_GUARD_MS
        );
      }, HOMEPAGE_HERO_END_HOLD_MS);
    },
    [
      beginCoveredLoopReset,
      clearLoopTransitionTimers,
      prefersReducedMotion,
      videoFailed,
    ]
  );

  const handleOpeningBridgeEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (
        event.target !== event.currentTarget ||
        (event.propertyName && event.propertyName !== "opacity")
      ) {
        return;
      }

      if (openingBridgeVisible && loopTransition === "covering") {
        const video = videoRef.current;
        if (video) beginCoveredLoopReset(video);
      } else if (
        !openingBridgeVisible &&
        loopTransition === "revealing"
      ) {
        finishLoopTransition();
      }
    },
    [
      beginCoveredLoopReset,
      finishLoopTransition,
      loopTransition,
      openingBridgeVisible,
    ]
  );

  useEffect(() => {
    const video = videoRef.current;
    const source = sourceRef.current;
    if (!videoLoadEnabled || !video || !source) return undefined;

    source.addEventListener("error", handleVideoError);
    const noSourceCheck = window.setTimeout(() => {
      if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
        handleVideoError();
      }
    }, 0);

    return () => {
      window.clearTimeout(noSourceCheck);
      source.removeEventListener("error", handleVideoError);
    };
  }, [handleVideoError, videoLoadEnabled]);

  useEffect(() => {
    if (!videoLoadEnabled) return;
    videoRef.current?.load();
  }, [videoLoadEnabled]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!motionPreferenceResolved) {
      pausePlayback();
      return;
    }

    if (prefersReducedMotion) {
      clearLoopTransitionTimers();
      openingResetStartedRef.current = false;
      loopTransitionActiveRef.current = false;
      pausePlayback();
      const timeoutId = window.setTimeout(() => {
        setLoopTransition("idle");
        setOpeningBridgeVisible(false);
        setAutoplayBlocked(false);
        setBrandVisible(false);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }

    const timeoutId = window.setTimeout(attemptPlayback, 0);
    return () => window.clearTimeout(timeoutId);
  }, [
    attemptPlayback,
    clearLoopTransitionTimers,
    motionPreferenceResolved,
    pausePlayback,
    prefersReducedMotion,
  ]);

  useEffect(() => {
    const hero = heroRef.current;
    const video = videoRef.current;
    if (!hero || !video) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      const heroBox = hero.getBoundingClientRect();
      heroVisibleRef.current =
        heroBox.height === 0 ||
        (heroBox.bottom > 0 && heroBox.top < window.innerHeight);
      if (
        heroVisibleRef.current &&
        documentLoaded &&
        motionPreferenceResolved &&
        !prefersReducedMotion
      ) {
        const timeoutId = window.setTimeout(() => {
          setVideoLoadEnabled(true);
        }, 0);
        return () => window.clearTimeout(timeoutId);
      }
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const substantiallyVisible =
          entry.isIntersecting && entry.intersectionRatio >= 0.15;
        heroVisibleRef.current = substantiallyVisible;

        if (substantiallyVisible) {
          if (resetResumeRef.current) {
            const resumeReset = resetResumeRef.current;
            resetResumeRef.current = null;
            resumeReset();
            return;
          }
          if (
            documentLoaded &&
            motionPreferenceResolved &&
            !prefersReducedMotion
          ) {
            setVideoLoadEnabled(true);
            attemptPlayback();
          }
        } else {
          pausePlayback();
        }
      },
      { threshold: [0, 0.15, 0.35] }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, [
    attemptPlayback,
    documentLoaded,
    motionPreferenceResolved,
    pausePlayback,
    prefersReducedMotion,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        pausePlayback();
        return;
      }
      if (resetResumeRef.current) {
        const resumeReset = resetResumeRef.current;
        resetResumeRef.current = null;
        resumeReset();
        return;
      }
      attemptPlayback();
      syncBrandReveal(video);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [attemptPlayback, pausePlayback, syncBrandReveal]);

  useEffect(
    () => () => {
      clearLoopTransitionTimers();
    },
    [clearLoopTransitionTimers]
  );

  return (
    <section
      ref={heroRef}
      className={`${styles.hero} theme-lock`}
      data-testid="home-hero"
      data-homepage-video-hero="1"
      data-video-ready={videoReady ? "true" : "false"}
      data-video-playing={videoPlaying ? "true" : "false"}
      data-video-failed={videoFailed ? "true" : "false"}
      data-motion-reduced={
        motionPreference === "unknown"
          ? "unknown"
          : prefersReducedMotion
            ? "true"
            : "false"
      }
      data-static-fallback={staticFallback ? "true" : "false"}
      data-fallback-visible={staticFallback ? "true" : "false"}
      data-opening-bridge={
        openingBridgeVisible || (!videoReady && !staticFallback)
          ? "true"
          : "false"
      }
      data-brand-visible={
        brandVisible || staticFallback ? "true" : "false"
      }
      data-loop-transition={loopTransition}
      aria-labelledby="homepage-video-hero-heading"
    >
      <div
        className={styles.poster}
        data-testid="homepage-hero-fallback"
        aria-hidden="true"
      />
      <noscript>
        <div className={styles.noScriptPoster} aria-hidden="true" />
      </noscript>
      <video
        ref={videoRef}
        className={styles.video}
        data-testid="homepage-hero-video"
        autoPlay={
          motionPreferenceResolved && !prefersReducedMotion
        }
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        disablePictureInPicture
        onLoadStart={() => setVideoFailed(false)}
        onLoadedMetadata={(event) => {
          syncBrandReveal(event.currentTarget);
          attemptPlayback();
        }}
        onLoadedData={() => {
          setVideoReady(true);
          attemptPlayback();
        }}
        onCanPlay={attemptPlayback}
        onPlay={() => {
          playAttemptRef.current += 1;
          if (playbackRecoveryTimerRef.current !== null) {
            window.clearTimeout(playbackRecoveryTimerRef.current);
            playbackRecoveryTimerRef.current = null;
          }
          playbackFailureCountRef.current = 0;
          setAutoplayBlocked(false);
          setVideoPlaying(true);
        }}
        onPause={() => setVideoPlaying(false)}
        onTimeUpdate={(event) => syncBrandReveal(event.currentTarget)}
        onSeeking={(event) => syncBrandReveal(event.currentTarget)}
        onEnded={(event) => startConcealedLoopReset(event.currentTarget)}
        onError={handleVideoError}
      >
        {videoLoadEnabled ? (
          <source
            ref={sourceRef}
            src={HOMEPAGE_HERO_VIDEO_SRC}
            type="video/mp4"
            onError={handleVideoError}
          />
        ) : null}
      </video>

      <div className={styles.topGradient} aria-hidden="true" />
      <div className={styles.lowerGradient} aria-hidden="true" />
      <div className={styles.brandGradient} aria-hidden="true" />

      <div className={styles.content}>
        <div className={styles.copy} data-testid="homepage-hero-copy">
          <p className={styles.eyebrow}>Shop the neighborhood.</p>
          <h1 id="homepage-video-hero-heading" className={styles.heading}>
            Discover what’s nearby.
          </h1>
          <Link href="/nearby" className={styles.cta} prefetch={false}>
            Explore nearby
          </Link>
        </div>
      </div>

      <div
        className={styles.brandLockup}
        aria-hidden="true"
        data-testid="homepage-hero-brand"
      >
        <YourBarrioLogo
          variant="mark-white"
          alt=""
          aria-hidden="true"
          size={108}
          className={styles.brandMark}
          draggable={false}
        />
        <YourBarrioLogo
          variant="wordmark-dark"
          alt=""
          aria-hidden="true"
          width={238}
          height={48}
          className={styles.wordmark}
          draggable={false}
        />
      </div>
      <div
        className={styles.openingBridge}
        data-testid="homepage-hero-opening-bridge"
        style={{
          backgroundImage: `url("${HOMEPAGE_HERO_OPENING_FRAME_SRC}")`,
        }}
        aria-hidden="true"
        onTransitionEnd={handleOpeningBridgeEnd}
      />
    </section>
  );
}
